import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { to: "/services", label: "Services", id: "nav-link-services" },
  { to: "/case-studies", label: "Case Studies", id: "nav-link-case-studies" },
  { to: "/insights", label: "Insights", id: "nav-link-insights" },
  { to: "/about", label: "About", id: "nav-link-about" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b hairline py-3" : "bg-transparent py-5"
      }`}
      data-testid="site-navigation"
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 md:px-10">
        <Link to="/" data-testid="nav-link-home" aria-label="Vector Growth and Advisory home">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-9 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={({ isActive }) =>
                `link-underline font-mono text-[11px] uppercase tracking-[0.24em] transition-colors ${
                  isActive ? "text-neon" : "text-slate-500 hover:text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="group hidden items-center gap-2 rounded-full border border-neon/40 bg-neon/5 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-neon transition-all duration-300 hover:bg-neon hover:text-white hover:shadow-[0_0_30px_rgba(47,168,224,0.35)] sm:flex"
          >
            Schedule Advisory Call
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            className="flex h-10 w-10 items-center justify-center rounded-full border hairline text-slate-700 lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="glass fixed inset-x-0 top-0 z-[-1] flex min-h-screen flex-col justify-center gap-2 px-8 pt-20"
            data-testid="nav-mobile-menu"
          >
            <nav className="flex flex-col gap-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.to}
                    className="font-display text-4xl font-extrabold tracking-tight text-ink"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.36, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <Link
                  to="/contact"
                  data-testid="nav-mobile-cta"
                  className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-neon px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-ink"
                >
                  Schedule Advisory Call <ArrowUpRight className="h-4 w-4" />
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
